import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { LocalDepartments, LocalDepartmentItem, LocationSlugs } from '@appTypes/nafLocations';
import { actions } from '../redux/modules/nafLocation';
import useSelector from '../redux/typedHooks';

export const useGetNafLocations = (
  search: LocationSlugs,
  counties: string[],
  initialTake?: number,
  options?: { skip?: boolean; ignoreCounties?: boolean },
) => {
  const dispatch = useDispatch();
  const {
    data: locations,
    totalCount,
    error,
    fetchState,
    previousTake,
    previousCounties,
  } = useSelector((state) => state.nafLocation.locations[search]);

  const [take, setTake] = useState(previousTake || initialTake || 10);

  useEffect(() => {
    if (options?.skip) return;
    if (fetchState === 'isUpdating' || fetchState === 'error') return;

    const wantFlat = options?.ignoreCounties === true;
    const hasLocations = Array.isArray(locations) && locations.length > 0;
    const haveFlat = hasLocations && !(locations as any[])[0]?.county;
    const haveGrouped = hasLocations && Boolean((locations as any[])[0]?.county);
    const shapeMismatch = hasLocations && ((wantFlat && haveGrouped) || (!wantFlat && haveFlat));

    const requestedCounties = counties.length ? counties : undefined;
    const requestedCoveredByCache =
      previousCounties === undefined
        ? true
        : requestedCounties !== undefined && requestedCounties.every((slug) => previousCounties.includes(slug));
    const countiesMismatch = hasLocations && !requestedCoveredByCache;

    if (fetchState === 'initial' || shapeMismatch || countiesMismatch) {
      dispatch(
        actions.getNafLocations.request({
          search,
          take,
          counties: counties.length ? counties : undefined,
          ignoreCounties: options?.ignoreCounties,
        }),
      );
    }
  }, [
    counties,
    take,
    dispatch,
    search,
    options?.skip,
    options?.ignoreCounties,
    fetchState,
    locations,
    locations?.length,
    previousCounties,
  ]);

  const showMore = () => {
    setTake(take + 10);
  };

  useEffect(() => {
    if (initialTake) setTake(initialTake);
  }, [initialTake]);

  const nafLocationsByCounty = options?.ignoreCounties
    ? []
    : ([
        ...new Map(
          (Array.isArray(locations) ? (locations as any[]) : [])
            .filter((item) => item && item.county && item.county.slug)
            .map((county: LocalDepartments) => [county.county.slug, county]) as [string, LocalDepartments][],
        ).values(),
      ] as LocalDepartments[]);

  const filteredLocations = (nafLocationsByCounty || []).filter(
    ({ county, nafLocations }) => nafLocations.length > 0 && (counties.length === 0 || counties.includes(county.slug)),
  );

  const totalReturned = filteredLocations.reduce((acc, { nafLocations }) => acc + nafLocations.length, 0);

  const totalCountInSelectedCounties =
    counties.length === 0
      ? nafLocationsByCounty.reduce((acc, { county }) => acc + county.total, 0)
      : nafLocationsByCounty
          .filter(({ county }) => counties.some((selected) => selected === county.slug))
          .reduce((acc, { county }) => acc + county.total, 0);

  const isFiltering = totalCountInSelectedCounties !== totalCount;

  const flatItems: LocalDepartmentItem[] = options?.ignoreCounties ? (locations as LocalDepartmentItem[]) || [] : [];

  return {
    nafLocationsByCounty,
    filteredLocations,
    totalCount,
    totalReturned,
    showMore,
    fetchState,
    error,
    totalCountInSelectedCounties,
    isFiltering,
    flatItems,
  };
};
